import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { EyeIcon, MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import BlogImage from '../../components/common/BlogImage'
import RichTextContent from '../../components/common/RichTextContent'
import { Blog } from '../../types'
import { blogsApi } from '../../services/api'

const PAGE_SIZE = 10

const formatDate = (value?: string) => {
  if (!value) return '-'
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const statusBadge = (status: string) => {
  if (status === 'published') return 'bg-success-500/15 text-success-400 border-success-500/30'
  return 'bg-dark-700 text-gray-300 border-dark-600'
}

const BlogManagementPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [submittingId, setSubmittingId] = useState<number | null>(null)
  const [items, setItems] = useState<Blog[]>([])
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published'>('all')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [preview, setPreview] = useState<Blog | null>(null)

  const load = () => {
    setLoading(true)
    setError('')
    blogsApi.adminListBlogs({
      page,
      page_size: PAGE_SIZE,
      search: search || undefined,
      category: categoryFilter || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
    })
      .then((res) => {
        setItems(res.items)
        setTotalPages(res.total_pages)
      })
      .catch(() => {
        setItems([])
        setTotalPages(0)
        setError('Failed to fetch blogs. Please refresh.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [page, search, statusFilter, categoryFilter])

  const categories = useMemo(() => {
    return Array.from(new Set(items.map(i => (i.category || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b))
  }, [items])

  const onDelete = async (id: number) => {
    if (!window.confirm('Delete this blog permanently?')) return
    setSubmittingId(id)
    try {
      await blogsApi.adminDeleteBlog(id)
      load()
    } finally {
      setSubmittingId(null)
    }
  }

  const onPublishToggle = async (blog: Blog) => {
    setSubmittingId(blog.id)
    try {
      if (blog.status === 'published') await blogsApi.adminUnpublishBlog(blog.id)
      else await blogsApi.adminPublishBlog(blog.id)
      load()
    } finally {
      setSubmittingId(null)
    }
  }

  const onPreview = async (blog: Blog) => {
    setSubmittingId(blog.id)
    try {
      const full = await blogsApi.adminPreviewBlog(blog.id)
      setPreview(full)
    } finally {
      setSubmittingId(null)
    }
  }

  const canPrev = page > 1
  const canNext = page < totalPages

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Blog Management</h1>
          <p className="text-gray-400 mt-1">Create, update, publish, and manage all blog content.</p>
        </div>
        <Link to="/admin/blogs/new">
          <Button>
            <PlusIcon className="w-4 h-4 mr-2" />
            Create Blog
          </Button>
        </Link>
      </div>

      <Card>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_200px_200px_auto] gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setPage(1)
                  setSearch(searchInput.trim())
                }
              }}
              placeholder="Search title, excerpt, tags"
              className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-dark-800 border border-dark-600 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={e => { setPage(1); setCategoryFilter(e.target.value) }}
            className="px-3 py-2.5 rounded-lg bg-dark-800 border border-dark-600 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select
            value={statusFilter}
            onChange={e => { setPage(1); setStatusFilter(e.target.value as any) }}
            className="px-3 py-2.5 rounded-lg bg-dark-800 border border-dark-600 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setPage(1); setSearch(searchInput.trim()) }}>
              Apply
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPage(1)
                setSearch('')
                setSearchInput('')
                setStatusFilter('all')
                setCategoryFilter('')
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="py-20 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <Card>
          <p className="text-danger-400">{error}</p>
        </Card>
      ) : (
        <Card padding={false}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead className="bg-dark-900 border-b border-dark-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-400">Title</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-400">Category</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-400">Author</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wide text-gray-400">Published</th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wide text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">No blogs found.</td>
                  </tr>
                )}

                {items.map((blog) => (
                  <tr key={blog.id} className="hover:bg-dark-900/40">
                    <td className="px-4 py-4 align-top">
                      <div className="mb-3 h-16 w-28 overflow-hidden rounded-md border border-dark-700 bg-dark-800">
                        <BlogImage
                          src={blog.featured_image}
                          alt={blog.title}
                          className="w-full h-full object-cover"
                          fallbackClassName="w-full h-full flex items-center justify-center text-[10px] text-gray-500"
                          fallbackText="No image"
                        />
                      </div>
                      <p className="text-white font-semibold">{blog.title}</p>
                      <p className="text-xs text-gray-500 mt-1">/{blog.slug}</p>
                      <p className="text-xs text-gray-400 mt-2 line-clamp-2">{blog.short_description || blog.summary || '-'}</p>
                    </td>
                    <td className="px-4 py-4 text-gray-300">{blog.category || '-'}</td>
                    <td className="px-4 py-4 text-gray-300">{blog.author || '-'}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs border ${statusBadge(blog.status)}`}>
                        {blog.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-400 text-sm">{formatDate(blog.published_at)}</td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="p-2 rounded-lg border border-dark-600 text-gray-300 hover:text-white hover:border-dark-500"
                          onClick={() => onPreview(blog)}
                          disabled={submittingId === blog.id}
                          title="Preview"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>

                        <Link to={`/admin/blogs/${blog.id}/edit`} className="p-2 rounded-lg border border-dark-600 text-gray-300 hover:text-white hover:border-dark-500">
                          <PencilSquareIcon className="w-4 h-4" />
                        </Link>

                        <button
                          type="button"
                          onClick={() => onPublishToggle(blog)}
                          disabled={submittingId === blog.id}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${
                            blog.status === 'published'
                              ? 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10'
                              : 'border-success-500/40 text-success-300 hover:bg-success-500/10'
                          }`}
                        >
                          {blog.status === 'published' ? 'Unpublish' : 'Publish'}
                        </button>

                        <button
                          type="button"
                          onClick={() => onDelete(blog.id)}
                          disabled={submittingId === blog.id}
                          className="p-2 rounded-lg border border-danger-500/40 text-danger-300 hover:bg-danger-500/10"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={!canPrev} onClick={() => setPage(p => p - 1)}>
            Previous
          </Button>
          <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
          <Button size="sm" variant="outline" disabled={!canNext} onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-4xl max-h-[90vh] overflow-auto rounded-2xl border border-dark-700 bg-dark-900" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-dark-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Preview: {preview.title}</h2>
              <button type="button" className="text-gray-400 hover:text-white" onClick={() => setPreview(null)}>Close</button>
            </div>

            <div className="p-6">
              <div className="w-full h-64 rounded-xl border border-dark-700 bg-dark-800 overflow-hidden">
                <BlogImage
                  src={preview.featured_image}
                  alt={preview.title}
                  className="w-full h-full object-cover"
                  fallbackClassName="w-full h-full flex items-center justify-center text-sm text-gray-500"
                  fallbackText="Image unavailable"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded bg-dark-700 text-gray-300">{preview.status}</span>
                {preview.category && <span className="px-2 py-1 rounded bg-primary-500/15 text-primary-300">{preview.category}</span>}
              </div>
              <p className="mt-4 text-sm text-gray-300">{preview.short_description || preview.summary}</p>
              <RichTextContent html={preview.content} className="mt-6 prose prose-invert prose-p:text-gray-300 prose-headings:text-white" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BlogManagementPage
